import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FailureDiagnostic } from './failure-diagnostics';

type Pending = { diagnostic: Pick<FailureDiagnostic, 'code' | 'step' | 'status' | 'time' | 'reference' | 'clientError' | 'clientSource' | 'online' | 'visibility'> & { version: string }; attempts: number };

@Injectable({ providedIn: 'root' })
export class DiagnosticReporterService {
  private readonly key = 'spiel-diagnostics-v25';
  private http: HttpClient;
  private pending: Pending[] = [];
  private sending = false;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private onOnline = () => { void this.flush(); };

  constructor(backend: HttpBackend) {
    // Bypass the application's error interceptor: reporting must never produce a new error page.
    this.http = new HttpClient(backend);
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.key) || '[]');
      if (Array.isArray(stored)) this.pending = stored.filter(item =>
        item?.diagnostic?.version === '2.5' && typeof item.diagnostic.reference === 'string' &&
        Number.isInteger(item.attempts) && item.attempts >= 0 && item.attempts < 3 &&
        Date.now() - Date.parse(item.diagnostic.time) < 86400000
      ).slice(-5);
    } catch { /* Storage may be disabled. In-memory delivery still works. */ }
    window.addEventListener('online', this.onOnline);
    void this.flush();
  }

  report(diagnostic: FailureDiagnostic): void {
    if (this.pending.some(item => item.diagnostic.reference === diagnostic.reference)) return;
    const { code, step, status, time, reference, clientError, clientSource, online, visibility } = diagnostic;
    this.pending.push({ diagnostic: { code, step, status, time, reference, clientError, clientSource, online, visibility, version: '2.5' }, attempts: 0 });
    this.pending = this.pending.slice(-5);
    this.save();
    void this.flush();
  }

  private save(): void {
    try { sessionStorage.setItem(this.key, JSON.stringify(this.pending)); } catch { /* best effort */ }
  }

  private async flush(): Promise<void> {
    if (this.sending || navigator.onLine === false) return;
    this.sending = true;
    try {
      for (const item of [...this.pending]) {
        if (item.attempts >= 3) continue;
        item.attempts++; this.save(); // Persist before sending, including across manual reloads.
        try {
          await firstValueFrom(this.http.post(`${environment.API_SERVER_URL}/client-diagnostics/report`,
            { diagnostic: item.diagnostic }, { withCredentials: true }).pipe(timeout(5000)));
          this.pending = this.pending.filter(entry => entry !== item);
        } catch {
          if (item.attempts >= 3) this.pending = this.pending.filter(entry => entry !== item);
        }
        this.save();
      }
    } finally {
      this.sending = false;
      if (this.pending.length && !this.retryTimer) {
        this.retryTimer = setTimeout(() => { this.retryTimer = undefined; void this.flush(); }, 30000);
      }
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.retryTimer);
    window.removeEventListener('online', this.onOnline);
  }
}
